import { useEffect, useRef } from 'react';
import PhoneFrame from '../../components/PhoneFrame';
import UAEFlag from '../../components/UAEFlag';
import FootballPitch from '../../components/FootballPitch';

// Import screenshots from public folder – use process.env.PUBLIC_URL for CRA
const SS = (n) => `${process.env.PUBLIC_URL}/screenshots/phone_ss_${n}.jpg`;

export default function HeroSection() {
  const bgRef   = useRef(null);
  const skyRef  = useRef(null);
  const pitchRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      if (bgRef.current)    bgRef.current.style.transform    = `translateY(${y * 0.35}px)`;
      if (skyRef.current)   skyRef.current.style.transform   = `translateY(${y * 0.18}px)`;
      if (pitchRef.current) pitchRef.current.style.transform = `translateY(${y * 0.12}px) rotate(${y * 0.01}deg)`;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section style={{
      minHeight: '100vh', position: 'relative',
      overflow: 'hidden', display: 'flex', alignItems: 'center',
    }}>
      {/* ── Parallax layer 1 – radial glow ── */}
      <div ref={bgRef} style={{
        position: 'absolute', inset: '-20%', zIndex: 1, willChange: 'transform',
        background: 'radial-gradient(ellipse 80% 80% at 65% 50%, rgba(21,35,53,0.95) 0%, transparent 100%)',
        pointerEvents: 'none',
      }} />

      {/* ── Parallax layer 2 – Dubai skyline SVG path ── */}
      <div ref={skyRef} style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        height: '55%', zIndex: 2, willChange: 'transform', pointerEvents: 'none',
        background: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 1440 400'%3E%3Cpath fill='%23080F18' fill-opacity='0.97' d='M0,400 L0,280 L30,280 L30,240 L50,240 L50,200 L70,200 L70,220 L90,220 L90,180 L95,180 L95,160 L105,160 L105,140 L115,140 L115,160 L120,160 L120,180 L130,180 L130,160 L150,160 L150,100 L155,100 L155,80 L160,80 L160,60 L165,60 L165,55 L170,55 L170,60 L175,60 L175,80 L180,80 L180,100 L185,100 L185,160 L200,160 L200,140 L210,140 L210,100 L215,100 L215,90 L220,90 L220,100 L225,100 L225,140 L240,140 L240,120 L260,120 L260,100 L265,80 L270,100 L275,120 L290,120 L290,150 L310,150 L310,130 L320,130 L320,110 L330,110 L330,90 L340,90 L340,110 L350,110 L350,130 L360,130 L360,150 L380,150 L380,120 L400,120 L400,80 L405,80 L405,60 L415,60 L415,40 L420,40 L420,20 L425,15 L430,20 L430,40 L435,40 L435,60 L445,60 L445,80 L450,80 L450,120 L470,120 L470,140 L490,140 L490,100 L500,100 L500,80 L510,80 L510,100 L530,100 L530,130 L550,130 L550,110 L570,110 L570,90 L580,70 L590,90 L600,110 L610,110 L610,130 L630,130 L630,160 L660,160 L660,140 L680,140 L680,120 L700,120 L700,100 L720,100 L720,120 L730,120 L730,140 L750,140 L750,160 L780,160 L780,130 L800,130 L800,100 L810,80 L820,60 L830,80 L840,100 L850,130 L870,130 L870,150 L900,150 L900,120 L920,100 L940,80 L950,70 L960,80 L970,100 L980,120 L1000,120 L1000,140 L1020,140 L1020,160 L1050,160 L1050,140 L1060,120 L1070,100 L1080,120 L1090,140 L1100,160 L1120,160 L1120,130 L1140,110 L1160,90 L1170,70 L1180,90 L1190,110 L1200,130 L1210,160 L1240,160 L1240,140 L1260,140 L1260,120 L1280,100 L1290,80 L1300,100 L1310,120 L1320,140 L1340,140 L1340,160 L1360,160 L1380,180 L1400,200 L1440,200 L1440,400 Z'/%3E%3C/svg%3E") no-repeat bottom center`,
        backgroundSize: 'cover',
      }} />

      {/* ── Parallax layer 3 – faint pitch overlay ── */}
      <div ref={pitchRef} style={{
        position: 'absolute', right: '-5%', top: '5%',
        width: '52%', height: '90%', zIndex: 2,
        opacity: 0.1, pointerEvents: 'none', willChange: 'transform',
      }}>
        <FootballPitch />
      </div>

      {/* ── Coral light flare ── */}
      <div style={{
        position: 'absolute', top: '10%', right: '28%',
        width: 600, height: 600, borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(232,96,74,0.12) 0%, transparent 70%)',
        zIndex: 2, pointerEvents: 'none',
      }} />

      {/* ── CONTENT ── */}
      <div style={{
        position: 'relative', zIndex: 5,
        maxWidth: 1280, margin: '0 auto', width: '100%',
        padding: '120px clamp(1rem,4vw,3rem) 6rem',
        display: 'grid', gridTemplateColumns: '1fr 1fr',
        gap: '3rem', alignItems: 'center',
      }}>

        {/* ── LEFT COPY ── */}
        <div>
          {/* UAE badge */}
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: 'rgba(13,27,42,0.8)', backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255,255,255,0.1)', borderRadius: 40,
            padding: '6px 14px 6px 6px', marginBottom: '1.5rem',
            animation: 'fadeLeft 0.8s ease forwards', opacity: 0,
          }}>
            <UAEFlag width={28} />
            <span style={{ fontFamily: 'var(--font-m)', fontSize: 10, color: 'rgba(255,255,255,0.7)', letterSpacing: 1 }}>
              UAE • BUILT IN DUBAI
            </span>
          </div>

          {/* Headline */}
          <h1 style={{
            fontFamily: 'var(--font-d)', fontWeight: 900,
            lineHeight: 0.88, textTransform: 'uppercase',
            fontSize: 'clamp(62px,7.5vw,100px)',
            animation: 'fadeLeft 0.9s 0.15s ease forwards', opacity: 0,
          }}>
            <span style={{ display: 'block', color: 'var(--white)' }}>STOP</span>
            <span style={{ display: 'block', color: 'var(--white)' }}>TEXTING.</span>
            <span style={{ display: 'block', color: 'var(--coral)', textShadow: '0 0 60px rgba(232,96,74,0.5)', fontStyle: 'italic' }}>START</span>
            <span style={{ display: 'block', color: 'var(--coral)', textShadow: '0 0 60px rgba(232,96,74,0.5)', fontStyle: 'italic' }}>PLAYING.</span>
          </h1>

          {/* Sub */}
          <p style={{
            fontSize: 18, color: 'var(--muted)', lineHeight: 1.75,
            maxWidth: 480, margin: '2rem 0 2.5rem', fontWeight: 300,
            animation: 'fadeLeft 0.9s 0.3s ease forwards', opacity: 0,
          }}>
            Hadafi helps UAE football players organize games with their friends—no WhatsApp chaos, no hidden fees, no strangers.
          </p>

          {/* CTA buttons */}
          <div style={{
            display: 'flex', gap: '1rem', flexWrap: 'wrap',
            animation: 'fadeLeft 0.9s 0.45s ease forwards', opacity: 0,
          }}>
            <button className="shimmer-btn" style={{
              background: 'var(--coral)', border: 'none',
              fontFamily: 'var(--font-d)', fontSize: 16, fontWeight: 700,
              letterSpacing: 2, textTransform: 'uppercase', color: '#fff',
              padding: '16px 36px', borderRadius: 4,
              boxShadow: '0 8px 30px rgba(232,96,74,0.4)',
              animation: 'glowPulse 3s ease infinite 2s',
            }}>⬇ Download Free</button>

            <button style={{
              background: 'transparent',
              border: '1px solid rgba(240,244,248,0.18)',
              fontFamily: 'var(--font-d)', fontSize: 16, fontWeight: 700,
              letterSpacing: 2, textTransform: 'uppercase', color: 'var(--white)',
              padding: '16px 36px', borderRadius: 4, cursor: 'pointer',
              transition: 'border-color 0.2s',
            }}
              onMouseEnter={e => e.target.style.borderColor = 'var(--coral)'}
              onMouseLeave={e => e.target.style.borderColor = 'rgba(240,244,248,0.18)'}
            >Watch Demo ▷</button>
          </div>

          {/* Store badges */}
          <div style={{
            display: 'flex', gap: '1rem', marginTop: '2rem', flexWrap: 'wrap',
            animation: 'fadeLeft 0.9s 0.6s ease forwards', opacity: 0,
          }}>
            {[
              { store: 'App Store',    icon: '🍎' },
              { store: 'Google Play',  icon: '▶' },
            ].map(({ store, icon }) => (
              <div key={store} style={{
                display: 'flex', alignItems: 'center', gap: 10,
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: 8, padding: '10px 18px', cursor: 'pointer',
                transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
              >
                <span style={{ fontSize: 20 }}>{icon}</span>
                <div>
                  <div style={{ fontFamily: 'var(--font-b)', fontSize: 9, color: 'var(--muted)', letterSpacing: 1, textTransform: 'uppercase' }}>Download on</div>
                  <div style={{ fontFamily: 'var(--font-d)', fontSize: 15, fontWeight: 700, letterSpacing: 1 }}>{store}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── RIGHT PHONES ── */}
        <div style={{ position: 'relative', height: 560, display: 'flex', justifyContent: 'center' }}>
          {/* Orbit ring */}
          <div style={{
            position: 'absolute', top: '30%', left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 300, height: 300, borderRadius: '50%',
            border: '1px solid rgba(232,96,74,0.1)',
            animation: 'rotateOrbit 20s linear infinite',
            pointerEvents: 'none', zIndex: 0,
          }}>
            {[0,60,120,180,240,300].map((angle) => (
              <div key={angle} style={{
                position: 'absolute', top: '50%', left: '50%',
                width: 6, height: 6, borderRadius: '50%',
                background: 'var(--coral)', opacity: 0.5,
                transform: `rotate(${angle}deg) translateX(148px) translate(-50%,-50%)`,
              }} />
            ))}
          </div>

          {/* Phone 1 */}
          <PhoneFrame src={SS(2)} glowColor="rgba(232,96,74,0.4)"
            style={{ position: 'absolute', top: 0, left: '8%', animation: 'float3 5s ease-in-out infinite', zIndex: 2 }}
          />
          {/* Phone 2 */}
          <PhoneFrame src={SS(4)} glowColor="rgba(212,168,71,0.3)"
            style={{ position: 'absolute', top: 60, left: '48%', animation: 'float1 4.5s 0.5s ease-in-out infinite', zIndex: 2 }}
          />
          {/* Phone 3 – front */}
          <PhoneFrame src={SS(5)} glowColor="rgba(232,96,74,0.55)"
            style={{ position: 'absolute', top: 180, left: '26%', animation: 'float2 5.5s 1s ease-in-out infinite', zIndex: 5 }}
          />
        </div>
      </div>

      {/* ── Scroll hint ── */}
      <div style={{
        position: 'absolute', bottom: 40, left: '50%',
        transform: 'translateX(-50%)', zIndex: 10, textAlign: 'center',
      }}>
        <div style={{ fontFamily: 'var(--font-m)', fontSize: 10, color: 'var(--muted)', letterSpacing: 2, marginBottom: 8 }}>SCROLL</div>
        <div style={{ width: 1, height: 40, background: 'linear-gradient(to bottom,var(--coral),transparent)', margin: '0 auto' }} />
      </div>
    </section>
  );
}
